<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  endpointId: { type: String, default: 'create' },
  pathParams: { type: Object, default: () => ({}) },
  queryParams: { type: Object, default: () => ({}) },
  payload: { type: Object, default: () => ({}) },
  accessToken: { type: String, default: '' },
});

const route = useRoute();
const { t } = useI18n();

const method = computed(
  () =>
    ({
      create: 'POST',
      list: 'GET',
      list_upcoming: 'GET',
      confirm: 'POST',
      update: 'PATCH',
      destroy: 'DELETE',
      create_human_attention: 'POST',
    })[props.endpointId]
);

const base = computed(() => {
  const a = route.params.accountId;
  const origin = window.location.origin;
  if (props.endpointId === 'create_human_attention') {
    return `${origin}/api/v1/accounts/${a}/painel/human_attention_requests`;
  }
  let p = `${origin}/api/v1/accounts/${a}/painel/appointments`;
  if (['update', 'destroy', 'confirm'].includes(props.endpointId)) {
    p += `/${props.pathParams?.id || ':id'}`;
    if (props.endpointId === 'confirm') p += '/confirm';
  } else if (props.endpointId === 'list_upcoming') {
    p += '/upcoming';
  }
  const qs = new URLSearchParams(props.queryParams || {}).toString();
  return qs ? `${p}?${qs}` : p;
});

const curl = computed(() => {
  const headers = [
    `-H 'api_access_token: ${props.accessToken || 'SEU_TOKEN'}'`,
    `-H 'Content-Type: application/json'`,
  ].join(' \\\n  ');
  const body =
    method.value === 'GET' || method.value === 'DELETE'
      ? ''
      : ` \\\n  -d '${JSON.stringify(props.payload || {}, null, 2)}'`;
  return `curl -X ${method.value} '${base.value}' \\\n  ${headers}${body}`;
});

const urlDisplay = computed(() => `${method.value} ${base.value}`);

const copy = s => navigator.clipboard.writeText(s);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <div class="mb-1 flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-10">{{
          t('PAINEL.API_PLAYGROUND.LABEL_URL')
        }}</span>
        <button
          class="text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="copy(base)"
        >
          {{ t('PAINEL.API_PLAYGROUND.COPY') }}
        </button>
      </div>
      <div class="overflow-x-auto">
        <pre class="rounded bg-n-slate-2 p-2 text-xs">{{ urlDisplay }}</pre>
      </div>
    </div>
    <div v-if="method !== 'GET' && method !== 'DELETE'">
      <div class="mb-1 flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-10">{{
          t('PAINEL.API_PLAYGROUND.LABEL_JSON')
        }}</span>
        <button
          class="text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="copy(JSON.stringify(payload, null, 2))"
        >
          {{ t('PAINEL.API_PLAYGROUND.COPY') }}
        </button>
      </div>
      <pre class="overflow-x-auto rounded bg-n-slate-2 p-2 text-xs">{{
        JSON.stringify(payload, null, 2)
      }}</pre>
    </div>
    <div>
      <div class="mb-1 flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-10">{{
          t('PAINEL.API_PLAYGROUND.LABEL_CURL')
        }}</span>
        <button
          class="text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="copy(curl)"
        >
          {{ t('PAINEL.API_PLAYGROUND.COPY') }}
        </button>
      </div>
      <pre class="whitespace-pre-wrap rounded bg-n-slate-2 p-2 text-xs">{{
        curl
      }}</pre>
    </div>
  </div>
</template>
